import React, { useState } from 'react';
import { Table, Tag, Button, Select, Skeleton } from 'antd';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Option } = Select;

const PayslipTab = ({ employeeId }) => {
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [loading, setLoading] = useState(false);

  // Sample data - replace with actual API call
  const payslipData = [
    {
      id: 1,
      month: 'January 2024',
      payPeriod: '01 Jan - 31 Jan 2024',
      basicSalary: 50000,
      allowances: 15000,
      deductions: 5000,
      netSalary: 60000,
      status: 'paid',
      paidDate: '2024-02-05',
    },
    {
      id: 2,
      month: 'December 2023',
      payPeriod: '01 Dec - 31 Dec 2023',
      basicSalary: 50000,
      allowances: 15000,
      deductions: 5000,
      netSalary: 60000,
      status: 'paid',
      paidDate: '2024-01-05',
    },
    {
      id: 3,
      month: 'November 2023',
      payPeriod: '01 Nov - 30 Nov 2023',
      basicSalary: 50000,
      allowances: 15000,
      deductions: 5000,
      netSalary: 60000,
      status: 'paid',
      paidDate: '2023-12-05',
    },
  ];

  const columns = [
    {
      title: 'Month',
      dataIndex: 'month',
      key: 'month',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Pay Period',
      dataIndex: 'payPeriod',
      key: 'payPeriod',
    },
    {
      title: 'Basic Salary',
      dataIndex: 'basicSalary',
      key: 'basicSalary',
      render: (amount) => `₦${amount.toLocaleString()}`,
    },
    {
      title: 'Allowances',
      dataIndex: 'allowances',
      key: 'allowances',
      render: (amount) => <span className="text-success">+₦{amount.toLocaleString()}</span>,
    },
    {
      title: 'Deductions',
      dataIndex: 'deductions',
      key: 'deductions',
      render: (amount) => <span className="text-danger">-₦{amount.toLocaleString()}</span>,
    },
    {
      title: 'Net Salary',
      dataIndex: 'netSalary',
      key: 'netSalary',
      render: (amount) => <strong>₦{amount.toLocaleString()}</strong>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          paid: 'success',
          pending: 'warning',
          processing: 'processing',
        };
        return <Tag color={colors[status]}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Paid Date',
      dataIndex: 'paidDate',
      key: 'paidDate',
      render: (date) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <div className="dropdown dropdown-action text-end">
          <Button.Group>
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewPayslip(record.id)}
            >
              View
            </Button>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadPayslip(record.id)}
            >
              Download
            </Button>
          </Button.Group>
        </div>
      ),
    },
  ];

  const handleViewPayslip = (id) => {
    // TODO: Open payslip modal or navigate to payslip detail page
    console.log('Viewing payslip:', id);
  };

  const handleDownloadPayslip = (id) => {
    // TODO: Implement download functionality
    console.log('Downloading payslip:', id);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setLoading(true);
    // TODO: Fetch payslips for selected year
    setTimeout(() => setLoading(false), 1000);
  };

  const years = Array.from({ length: 5 }, (_, i) => dayjs().year() - i);

  if (!employeeId) {
    return null;
  }

  return (
    <div className="tab-pane fade" id="emp_assets">
      <div className="card">
        <div className="card-body">
          <div className="row mb-4">
            <div className="col-md-6">
              <h3 className="card-title">Payslip History</h3>
            </div>
            <div className="col-md-6 text-end">
              <Select
                value={selectedYear}
                onChange={handleYearChange}
                style={{ width: 120 }}
              >
                {years.map(year => (
                  <Option key={year} value={year}>{year}</Option>
                ))}
              </Select>
            </div>
          </div>

          {/* Summary Section */}
          <div className="row mb-4">
            <div className="col-md-3">
              <div className="card bg-light">
                <div className="card-body">
                  <h6 className="text-muted">Total Earned (YTD)</h6>
                  <h4>₦720,000</h4>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-light">
                <div className="card-body">
                  <h6 className="text-muted">Total Allowances</h6>
                  <h4 className="text-success">₦180,000</h4>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-light">
                <div className="card-body">
                  <h6 className="text-muted">Total Deductions</h6>
                  <h4 className="text-danger">₦60,000</h4>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-light">
                <div className="card-body">
                  <h6 className="text-muted">Net Paid</h6>
                  <h4>₦840,000</h4>
                </div>
              </div>
            </div>
          </div>

          {/* Payslip Table */}
          <div className="table-responsive">
            {loading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
              <Table
                columns={columns}
                dataSource={payslipData}
                rowKey="id"
                pagination={{
                  pageSize: 12,
                  showTotal: (total) => `Total ${total} payslips`,
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PayslipTab;
